import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/authUtils';
import { useLanguage } from '../../utils/LanguageContext';

const SignIn = ({ onToggleForm, redirectTo = '/app' }) => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const content = {
    en: {
      title: 'Sign In',
      subtitle: 'Welcome back to RumahKira',
      emailLabel: 'Email',
      passwordLabel: 'Password',
      signInBtn: 'Sign In',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      signUp: 'Sign up',
      invalidCreds: 'Invalid email or password',
      fillFields: 'Please fill in all fields',
    },
    my: {
      title: 'Log Masuk',
      subtitle: 'Selamat kembali ke RumahKira',
      emailLabel: 'Emel',
      passwordLabel: 'Kata Laluan',
      signInBtn: 'Log Masuk',
      forgotPassword: 'Lupa kata laluan?',
      noAccount: 'Tiada akaun?',
      signUp: 'Daftar',
      invalidCreds: 'Emel atau kata laluan tidak sah',
      fillFields: 'Sila isi semua medan',
    }
  };

  const t = content[language] || content.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(t.fillFields);
      return;
    }

    setLoading(true);
    try {
      await signIn(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(t.invalidCreds);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {t.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.subtitle}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          label={t.emailLabel}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoFocus
          margin="normal"
          size="small"
        />

        <TextField
          fullWidth
          label={t.passwordLabel}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          margin="normal"
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ textAlign: 'right', mt: 1 }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', fontWeight: 500 }}
            onClick={() => navigate('/auth/forgot-password')}
          >
            {t.forgotPassword}
          </Typography>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ mt: 2, mb: 2 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : t.signInBtn}
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="body2" align="center" sx={{ mt: 2 }}>
        {t.noAccount}{' '}
        <Typography
          component="span"
          variant="body2"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 500 }}
          onClick={onToggleForm}
        >
          {t.signUp}
        </Typography>
      </Typography>
    </Box>
  );
};

export default SignIn;
